//@author: nichole
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    forwardRef,
    input,
    model,
    ViewEncapsulation,
} from "@angular/core";
import { AngBridgeUtil } from "../../util/ang.bridge.util";
import { S25Util } from "../../util/s25-util";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { FormValueControl } from "@angular/forms/signals";
import { AriaLabel } from "../../util/aria/aria.label";

@TypeManagerDecorator("s25-toggle-button")
@Component({
    selector: "s25-toggle-button",
    template: ` <div>
        <div class="toggle-wrapper">
            <fieldset
                class="toggle-fieldset"
                role="radiogroup"
                [id]="id + id2"
                [attr.aria-labelledby]="ariaLabelledBy()"
            >
                @let valueIsTrue = sanitizedValue() === true;
                @let valueIsFalse = sanitizedValue() === false;

                @if (!ariaLabelledBy()) {
                    <legend class="visually-hidden">{{ legendText() }}</legend>
                }

                <div class="toggle toggle--private">
                    <input
                        [id]="id"
                        [attr.name]="id"
                        name="{{ id }}"
                        [ngClass]="{ ngChecked: valueIsFalse, ngUnchecked: valueIsTrue }"
                        class="toggle__radio toggle__radio--1"
                        type="radio"
                        [value]="false"
                        [ngModel]="sanitizedValue()"
                        (ngModelChange)="onChangeHandler($event)"
                        [disabled]="disabled()"
                    />

                    @if (sanitizedValue() === undefined) {
                        <input
                            [id]="id3"
                            [attr.name]="id"
                            name="{{ id }}"
                            class="toggle__radio toggle__radio--3 ngChecked"
                            type="radio"
                            [value]="undefined"
                            [ngModel]="sanitizedValue()"
                            (ngModelChange)="onChangeHandler($event)"
                            [disabled]="disabled()"
                        />
                        <label [for]="id3" class="visually-hidden">unset</label>
                    }

                    <input
                        [id]="id2"
                        [attr.name]="id"
                        name="{{ id }}"
                        [ngClass]="{ ngChecked: valueIsTrue, ngUnchecked: valueIsFalse }"
                        class="toggle__radio toggle__radio--2"
                        type="radio"
                        [value]="true"
                        [ngModel]="sanitizedValue()"
                        (ngModelChange)="onChangeHandler($event)"
                        [disabled]="disabled()"
                    />

                    <label [for]="id" class="toggle__label toggle__label--1">{{ falseLabel() }}</label>
                    <div class="toggle__knob" role="presentation"></div>
                    <label [for]="id2" class="toggle__label toggle__label--2">{{ trueLabel() }}</label>
                </div>
            </fieldset>
        </div>
    </div>`,
    styles: `
        s25-toggle-button fieldset.toggle-fieldset {
            width: fit-content;
            border: 0;
            margin: 0;
            padding: 0;
            min-inline-size: 0;
        }

        s25-toggle-button .visually-hidden {
            position: absolute;
            width: 1px;
            height: 1px;
            padding: 0;
            margin: -1px;
            overflow: hidden;
            clip: rect(0, 0, 0, 0);
            white-space: nowrap;
            border: 0;
        }

        s25-toggle-button input[disabled],
        s25-toggle-button input[disabled] ~ * {
            cursor: not-allowed !important;
        }

        s25-toggle-button input[disabled] ~ label {
            color: rgba(0, 0, 0, 0.5) !important;
        }

        s25-toggle-button input[disabled] ~ .toggle__knob {
            background-color: rgba(0, 0, 0, 0.26) !important;
        }

        s25-toggle-button input[disabled] ~ .toggle__knob::after {
            background-color: #e0e0e0 !important;
        }
    `,
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => S25ToggleButtonComponent), multi: true }],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25ToggleButtonComponent implements ControlValueAccessor, FormValueControl<boolean | undefined> {
    // even though we account for non-boolean values, we still WANT to make people to give booleans
    readonly value = model<boolean | undefined>();
    /**
     * Keeping this around for angularJS usages, which cannot bind directly to an output
     * @deprecated
     */
    readonly onChange = input<(value: boolean | undefined) => void>();
    readonly description = input("Toggle button", {
        transform: (value?: string) => value || "Toggle button",
    });
    readonly falseLabel = input("No", {
        transform: (value?: string) => value || "No",
    });
    readonly trueLabel = input("Yes", {
        transform: (value?: string) => value || "Yes",
    });
    readonly toggleLabel = input<AriaLabel>();
    readonly disabled = input(false);
    /**
     * We need to be overly defensive here, because there are some lazily typed implementations
     * that provide "F", "T", or who knows what else
     */
    readonly sanitizedValue = computed(() => this.sanitizeUnsafeBoolean(this.value()));
    readonly ariaLabelledBy = computed(() => AriaLabel.labelledBy(this.toggleLabel()));
    readonly legendText = computed(
        () => AriaLabel.text(this.toggleLabel()) ?? `${this.falseLabel()} or ${this.trueLabel()}`,
    );

    onChanged = (value?: boolean) => {};
    onTouched = () => {};

    static toggleCount: number = 1;
    readonly id: string = "ngToggleButton" + S25ToggleButtonComponent.toggleCount++;
    readonly id2: string = "ngToggleButton" + S25ToggleButtonComponent.toggleCount++;
    readonly id3: string = "ngToggleButton" + S25ToggleButtonComponent.toggleCount++;

    constructor(private cd: ChangeDetectorRef) {
        AngBridgeUtil.init(this);
    }

    public onChangeHandler(newValue: any) {
        const sanitized = this.sanitizeUnsafeBoolean(newValue);
        this.value.set(sanitized);
        if (this.onChange() && S25Util.isFunction(this.onChange())) {
            this.onChange()!(sanitized);
        }
        this.onTouched();
        this.onChanged?.(sanitized);
        // TODO: check and see if we need this. it should be unnecessary
        this.cd.detectChanges();
    }

    writeValue(value: boolean) {
        this.value.set(value);
    }

    registerOnChange(fn: any) {
        this.onChanged = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    private sanitizeUnsafeBoolean(value: unknown) {
        return S25Util.isUndefined(value) ? undefined : S25Util.toBool(value);
    }
}
